import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { UserResponse } from '@aannemerplus/types'
import { apiClient } from '@/api/client'
import router from '@/router'

const TOKEN_KEY = 'aannemerplus_token'

export const useAuthStore = defineStore('auth', () => {
  const token = ref<string | null>(localStorage.getItem(TOKEN_KEY))
  const user = ref<UserResponse | null>(null)

  const isAuthenticated = computed(() => !!token.value && !!user.value)
  const isAdmin = computed(() => user.value?.role === 'admin')

  function setToken(newToken: string) {
    token.value = newToken
    localStorage.setItem(TOKEN_KEY, newToken)
  }

  function clearToken() {
    token.value = null
    user.value = null
    localStorage.removeItem(TOKEN_KEY)
  }

  async function login(email: string, password: string) {
    const response = await apiClient.login({ email, password })
    const data = response.data
    setToken(data.token)
    user.value = data.user
  }

  // Passwordless login: for owners who claimed their site via email during onboarding
  // and never set a password. Mirrors login() but uses the email-code endpoint.
  async function loginWithCode(email: string, code: string) {
    const response = await apiClient.verifyLoginCode({ email, code })
    const data = response.data
    setToken(data.token)
    user.value = data.user
  }

  async function register(email: string, password: string, firstName: string, lastName: string) {
    const response = await apiClient.register({ email, password, firstName, lastName })
    const data = response.data
    setToken(data.token)
    user.value = data.user
  }

  function logout() {
    clearToken()
    router.push('/login')
  }

  function updateUser(data: Partial<UserResponse>) {
    if (user.value) {
      user.value = { ...user.value, ...data }
    }
  }

  async function initialize() {
    if (!token.value) return

    try {
      const response = await apiClient.getMe()
      user.value = response.data
    } catch {
      clearToken()
    }
  }

  return {
    token,
    user,
    isAuthenticated,
    isAdmin,
    setToken,
    login,
    loginWithCode,
    register,
    logout,
    updateUser,
    initialize
  }
})
